import React, { createContext, useState, useEffect, useContext } from 'react';
import { User, LoginResponse } from '../types/auth';
import { authService } from '../services/authService';
import { useBusinessTheme } from './BusinessThemeContext';
import { offlineService } from '../services/offlineService';
import { apiHealthService } from '../services/apiHealthService';
import { customUserDataFieldService } from '../services/customUserDataFieldService';
import { businessService } from '../services/businessService';
import { appointmentTypeService } from '../services/appointmentTypeService';
import { diagnosticsService } from '../services/diagnosticsService';

// Request deduplication for auth verification - prevents duplicate API calls during React StrictMode
let verifyPromise: Promise<{ ok: boolean; userData?: any }> | null = null;
let verifyPromiseTimestamp: number = 0;
const VERIFY_PROMISE_TTL = 5000; // Keep promise for 5 seconds to handle StrictMode remounts

/**
 * Pre-load essential data into IndexedDB after successful authentication.
 * Runs in the background (fire-and-forget) so it doesn't block the auth flow.
 * The services already save to IndexedDB internally when fetching from the API.
 */
const preloadIndexedDbData = (businessId?: string | null) => {
    const bizId = businessId || undefined;
    Promise.all([
        businessService.getBusinesses().then(async (businesses) => {
            const biz = businesses[0];
            if (biz) {
                await businessService.getBusinessUsers(biz.id);
            }
        }),
        appointmentTypeService.getAppointmentTypes(bizId),
    ]).catch(error => {
        console.error('Error pre-loading IndexedDB data:', error);
    });
};

export interface AuthContextType {
    isAuthenticated: boolean;
    user: User | null;
    businessId: string | null;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<boolean | LoginResponse>;
    register: (email: string, password: string, firstName: string, lastName: string) => Promise<LoginResponse>;
    logout: () => void;
    refreshToken: () => Promise<string | null>;
    hasBusinessSetup: boolean;
    refreshUser: () => Promise<User | null>;
    token: string;
    isOfflineAuth: boolean;
    needsReauth: boolean;
    sendOtp: (email: string) => Promise<{ success: boolean; message: string }>;
    loginWithOtp: (email: string, code: string) => Promise<boolean | LoginResponse>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const [businessId, setBusinessId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasBusinessSetup, setHasBusinessSetup] = useState<boolean>(false);
    const [isOfflineAuth, setIsOfflineAuth] = useState(false);
    const [needsReauth, setNeedsReauth] = useState(false);
    const { setBusinessId: setThemeBusinessId } = useBusinessTheme();

    // Helper function to decode JWT token and extract user ID
    const getUserIdFromToken = (token: string): string | null => {
        try {
            // JWT tokens have 3 parts separated by dots: header.payload.signature
            const parts = token.split('.');
            if (parts.length !== 3) {
                console.log('JWT token does not have 3 parts:', parts.length);
                return null;
            }
            
            // Decode the payload (second part)
            const payload = JSON.parse(atob(parts[1]));
            console.log('JWT payload:', payload);
            console.log('Looking for user ID in payload keys:', Object.keys(payload));
            
            // Try different possible keys for user ID
            const userId = payload.sub || payload.userId || payload.id || payload.nameid || payload.unique_name || 
                          payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] || null;
            console.log('Extracted user ID from JWT:', userId);
            return userId;
        } catch (error) {
            console.error('Error decoding JWT token:', error);
            return null;
        }
    };

    // Define initializeAuth function outside useEffect so it can be reused
    const initializeAuth = async () => {
        try {
            setIsLoading(true);
            // Clear any stale cached business data
            businessService.clearCache();
            // Check for token in localStorage first
            const token = localStorage.getItem('token');
            // Check offline status with fallback to navigator.onLine
            const offlineServiceStatus = offlineService.getOnlineStatus();
            const isOffline = !offlineServiceStatus || !navigator.onLine;
            
            console.log('AuthContext initializeAuth:', {
                hasToken: !!token,
                isOffline,
                navigatorOnline: navigator.onLine,
                offlineServiceStatus: offlineService.getOnlineStatus()
            });
            
            // Check what's currently in localStorage
            const currentUserId = localStorage.getItem('userId');
            console.log('Current userId in localStorage:', currentUserId);
            
            // If userId is not in localStorage or is 'cached-user', extract it from JWT and store it
            if (token && (!currentUserId || currentUserId === 'cached-user')) {
                console.log('userId not in localStorage or is cached-user, attempting to extract from JWT token...');
                const extractedUserId = getUserIdFromToken(token);
                if (extractedUserId) {
                    console.log('Extracted userId from JWT, storing in localStorage:', extractedUserId);
                    localStorage.setItem('userId', extractedUserId);
                }
            }
            
            if (token) {
                // Get cached data for fallback purposes
                const cachedUserEmail = localStorage.getItem('userEmail');
                const cachedUserName = localStorage.getItem('userName');
                const cachedBusinessId = localStorage.getItem('businessId');

                // Always try server verification first (unless we're definitely offline)
                if (!isOffline) {
                    try {
                        console.log("Attempting server verification for authentication");

                        // Use request deduplication to prevent multiple identical requests
                        const now = Date.now();
                        if (!verifyPromise || (now - verifyPromiseTimestamp) > VERIFY_PROMISE_TTL) {
                            verifyPromiseTimestamp = now;
                            verifyPromise = (async () => {
                                const response = await fetch(`${apiHealthService.getApiUrl()}/api/auth/verify`, {
                                    method: 'GET',
                                    headers: {
                                        'Authorization': `Bearer ${token}`,
                                        'Content-Type': 'application/json'
                                    }
                                });
                                if (response.ok) {
                                    const userData = await response.json();
                                    return { ok: true, userData };
                                }
                                return { ok: false };
                            })();
                        }

                        const result = await verifyPromise;

                        if (result.ok && result.userData) {
                            // Token is valid, get the user data
                            const userData = result.userData;
                            console.log("Token is valid, getting user data", userData);
                            setUser(userData.user);
                            setIsAuthenticated(true);
                            setIsOfflineAuth(false);
                            setNeedsReauth(false);
                            setHasBusinessSetup(!!userData.user.businessId);

                            // Cache user data for offline use
                            localStorage.setItem('userEmail', userData.user.email);
                            localStorage.setItem('userName', `${userData.user.firstName} ${userData.user.lastName}`);
                            if (userData.user.businessId) {
                                localStorage.setItem('businessId', userData.user.businessId);
                            }

                            if (userData.user.businessId) {
                                setBusinessId(userData.user.businessId);
                                setThemeBusinessId(userData.user.businessId);
                            }
                            diagnosticsService.start();
                            preloadIndexedDbData(userData.user.businessId);
                            return; // Exit early after successful server verification
                        }
                    } catch (error) {
                        console.log("Server verification failed, falling back to cached data", error);
                    }
                }

                // If we're offline or server verification failed, use cached data as fallback
                if (cachedUserEmail && cachedUserName) {
                    console.log("Using cached data for authentication");
                    const [firstName, lastName] = cachedUserName.split(' ');
                    // Try to get the actual user ID from localStorage, then from JWT token, fallback to cached-user
                    let cachedUserId = localStorage.getItem('userId');
                    console.log('Cached user ID from localStorage:', cachedUserId);
                    if (!cachedUserId && token) {
                        console.log('Attempting to extract user ID from JWT token...');
                        cachedUserId = getUserIdFromToken(token);
                        console.log('User ID extracted from JWT:', cachedUserId);
                    }
                    if (!cachedUserId) {
                        console.log('No user ID found, using fallback: cached-user');
                        cachedUserId = 'cached-user';
                    }
                    console.log('Final cached user ID:', cachedUserId);
                    const cachedUser: User = {
                        id: cachedUserId,
                        email: cachedUserEmail,
                        firstName: firstName || '',
                        lastName: lastName || '',
                        businessId: cachedBusinessId || null,
                        businessRoles: []
                    };
                    
                    setUser(cachedUser);
                    setIsAuthenticated(true);
                    setIsOfflineAuth(true);
                    setNeedsReauth(true);
                    setHasBusinessSetup(!!cachedBusinessId);
                    
                    if (cachedBusinessId) {
                        setBusinessId(cachedBusinessId);
                        setThemeBusinessId(cachedBusinessId);
                    }
                } else {
                    // No cached data available, clear auth state
                    localStorage.removeItem('token');
                    localStorage.removeItem('userEmail');
                    localStorage.removeItem('userName');
                    localStorage.removeItem('businessId');
                    setUser(null);
                    setIsAuthenticated(false);
                    setBusinessId(null);
                    setThemeBusinessId(null);
                }
            } else {
                // No token found, clear auth state
                setUser(null);
                setIsAuthenticated(false);
                setBusinessId(null);
                setThemeBusinessId(null);
            }
        } catch (error) {
            console.error('Error initializing auth:', error);
            
            // If we have a token and cached data, try to use it as fallback
            const token = localStorage.getItem('token');
            const cachedUserEmail = localStorage.getItem('userEmail');
            const cachedUserName = localStorage.getItem('userName');
            const cachedBusinessId = localStorage.getItem('businessId');
            
            if (token && cachedUserEmail && cachedUserName) {
                console.log("Using cached data as fallback after error");
                const [firstName, lastName] = cachedUserName.split(' ');
                const cachedUser: User = {
                    id: 'cached-user',
                    email: cachedUserEmail,
                    firstName: firstName || '',
                    lastName: lastName || '',
                    businessId: cachedBusinessId || null,
                    businessRoles: []
                };
                
                setUser(cachedUser);
                setIsAuthenticated(true);
                // Only set offline auth if we're actually offline
                const isOffline = !offlineService.getOnlineStatus() || !navigator.onLine;
                setIsOfflineAuth(isOffline);
                setNeedsReauth(true);
                setHasBusinessSetup(!!cachedBusinessId);
                
                if (cachedBusinessId) {
                    setBusinessId(cachedBusinessId);
                    setThemeBusinessId(cachedBusinessId);
                }
            } else {
                // No cached data available, clear auth state
                setUser(null);
                setIsAuthenticated(false);
                setBusinessId(null);
                setThemeBusinessId(null);
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        initializeAuth();
    }, [setThemeBusinessId]);

    // Listen for connectivity changes to handle re-authentication
    useEffect(() => {
        const handleConnectivityChange = (event: Event) => {
            const customEvent = event as CustomEvent;
            const isOnline = customEvent.detail.isOnline;
            
            console.log("Connectivity change detected:", { isOnline, currentIsOfflineAuth: isOfflineAuth });
            
            if (isOnline && isOfflineAuth) {
                console.log("Back online - attempting to re-authenticate");
                // When coming back online, try to re-authenticate
                initializeAuth();
            } else if (!isOnline && !isOfflineAuth) {
                console.log("Went offline - switching to offline auth mode");
                // When going offline, switch to offline auth mode if we have cached data
                const token = localStorage.getItem('token');
                const cachedUserEmail = localStorage.getItem('userEmail');
                const cachedUserName = localStorage.getItem('userName');
                const cachedBusinessId = localStorage.getItem('businessId');
                
                if (token && cachedUserEmail && cachedUserName) {
                    const [firstName, lastName] = cachedUserName.split(' ');
                    const cachedUserId = localStorage.getItem('userId') || getUserIdFromToken(token) || 'cached-user';
                    const cachedUser: User = {
                        id: cachedUserId,
                        email: cachedUserEmail,
                        firstName: firstName || '',
                        lastName: lastName || '',
                        businessId: cachedBusinessId || null,
                        businessRoles: []
                    };
                    
                    setUser(cachedUser);
                    setIsAuthenticated(true);
                    setIsOfflineAuth(true);
                    setNeedsReauth(true);
                    setHasBusinessSetup(!!cachedBusinessId);
                    
                    if (cachedBusinessId) {
                        setBusinessId(cachedBusinessId);
                        setThemeBusinessId(cachedBusinessId);
                    }
                }
            }
        };

        // Listen for offline/online events
        window.addEventListener('connectivity-changed', handleConnectivityChange);
        
        return () => {
            window.removeEventListener('connectivity-changed', handleConnectivityChange);
        };
    }, [isOfflineAuth, initializeAuth, setThemeBusinessId]);

    const login = async (email: string, password: string) => {
        try {
            const response = await authService.login(email, password);
            
            if (response.succeeded) {
                // Update the auth state
                setUser(response.user);
                setIsAuthenticated(true);
                setIsOfflineAuth(false);
                setNeedsReauth(false);
                setHasBusinessSetup(!!response.user.businessId);
                
                // Store authentication token
                localStorage.setItem('token', response.token);
                localStorage.setItem('userId', response.user.id);
                localStorage.setItem('userEmail', response.user.email);
                localStorage.setItem('userName', `${response.user.firstName} ${response.user.lastName}`);
                if (response.user.businessId) {
                    localStorage.setItem('businessId', response.user.businessId);
                }

                preloadIndexedDbData(response.user.businessId);

                return response;
            } else {
                // Return the error data instead of throwing a generic error
                // This allows the component to access the errors array
                console.log("Returning error data:", response);
                return response;
            }
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    };

    const sendOtp = async (email: string) => {
        return authService.sendOtp(email);
    };

    const loginWithOtp = async (email: string, code: string) => {
        try {
            const response = await authService.loginWithOtp(email, code);

            if (response.succeeded) {
                setUser(response.user);
                setIsAuthenticated(true);
                setIsOfflineAuth(false);
                setNeedsReauth(false);
                setHasBusinessSetup(!!response.user.businessId);

                localStorage.setItem('token', response.token);
                localStorage.setItem('userId', response.user.id);
                localStorage.setItem('userEmail', response.user.email);
                localStorage.setItem('userName', `${response.user.firstName} ${response.user.lastName}`);
                if (response.user.businessId) {
                    localStorage.setItem('businessId', response.user.businessId);
                }

                preloadIndexedDbData(response.user.businessId);

                return response;
            } else {
                console.log("Returning error data:", response);
                return response;
            }
        } catch (error) {
            console.error('OTP login error:', error);
            throw error;
        }
    };

    // Helper function to configure auth headers
    const configureAuthHeaders = (token: string) => {
        // If you're using axios, you'd set up an interceptor here
        // If using fetch directly, you might set up a global fetch wrapper
        // For now, just logging to confirm this was called
        console.log("Auth headers configured with token:", token?.substring(0, 10) + "...");
    };

    const register = async (email: string, password: string, firstName: string, lastName: string) => {
        try {
            const response = await authService.register(email, password, firstName, lastName);
            
            if (response.succeeded) {
                // Auto-login the user on successful registration
                setUser(response.user);
                setIsAuthenticated(true);
                setIsOfflineAuth(false);
                setNeedsReauth(false);
                setHasBusinessSetup(!!response.user.businessId);
                
                // Store authentication token
                localStorage.setItem('token', response.token);
                localStorage.setItem('userEmail', response.user.email);
                localStorage.setItem('userName', `${response.user.firstName} ${response.user.lastName}`);
                if (response.user.businessId) {
                    localStorage.setItem('businessId', response.user.businessId);
                }
                
                return response;
            } else {
                // Return the error data instead of throwing a generic error
                // This allows the component to access the errors array
                console.log("Returning error data:", response);
                return response;
            }
        } catch (error) {
            console.error('Registration error:', error);
            throw error;
        }
    };

    const logout = () => {
        setIsLoading(true);
        diagnosticsService.stop();
        authService.logout();
        customUserDataFieldService.clearCache(); // Clear cached user data fields
        setUser(null);
        setIsAuthenticated(false);
        setIsOfflineAuth(false);
        setNeedsReauth(false);
        setBusinessId(null);
        setThemeBusinessId(null);
        setIsLoading(false);
    };

    const refreshToken = async () => {
        try {
            const token = await authService.refreshToken();
            if (token) {
                setIsAuthenticated(true);
                setIsOfflineAuth(false);
                setNeedsReauth(false);
                const user = authService.getUserFromToken();
                setUser(user);
                setHasBusinessSetup(!!user?.businessId);
                return token;
            } else {
                logout();
                return null;
            }
        } catch (error) {
            console.error('Error refreshing token:', error);
            logout();
            return null;
        }
    };

    const refreshUser = async () => {
        try {
            const response = await authService.refreshUserData();
            if (response) {
                const user = response.user;
                
                setUser(user);
                setIsAuthenticated(true);
                setIsOfflineAuth(false);
                setNeedsReauth(false);
                
                // FORCE the business setup state to true if businessId exists
                if (user.businessId) {
                    console.log(`Business ID exists: ${user.businessId} - forcing hasBusinessSetup to true`);
                    setHasBusinessSetup(true);
                    setBusinessId(user.businessId);
                    setThemeBusinessId(user.businessId);
                } else {
                    console.log("No business ID found - forcing hasBusinessSetup to false");
                    setHasBusinessSetup(false);
                }
            }
            return user;
        } catch (error) {
            console.error('Error refreshing user:', error);
            return null;
        }
    };

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                user,
                businessId,
                isLoading,
                login,
                register,
                logout,
                refreshToken,
                hasBusinessSetup,
                refreshUser,
                token: localStorage.getItem('token') || '',
                isOfflineAuth,
                needsReauth,
                sendOtp,
                loginWithOtp,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}; 